import { Alert, Button, Skeleton, Typography } from '@mui/material';
import { IMaintenance } from 'interfaces';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { maintenanceService } from 'services/api';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { MAINTENANCE_TYPE_OPTIONS } from 'constants/maintenanceTypes';

type ExpiringMaintenancesWidgetBaseProps = {
  filterParams: Record<string, string>;
  translationKey: string;
  viewAllParams: Record<string, string>;
  useValidityAlert?: boolean;
};

function buildViewAllSearch(params: Record<string, string>) {
  const search = new URLSearchParams(params);
  const query = search.toString();
  return query ? `?${query}` : '';
}

function ExpiringMaintenancesWidgetBase({
  filterParams,
  translationKey,
  viewAllParams,
  useValidityAlert = false
}: ExpiringMaintenancesWidgetBaseProps) {
  const { t } = useTranslation();
  const [maintenances, setMaintenances] = useState<IMaintenance[]>();
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);
  const viewAllHref = `/tenant/equipments/maintenance/expiring${buildViewAllSearch(viewAllParams)}`;

  useEffect(() => {
    maintenanceService.getExpiringMaintenances(3, 0, filterParams).then((res) => {
      setMaintenances(res.results);
    });
  }, [filterKey, filterParams]);

  return (
    <div>
      <Typography sx={{ fontSize: 14 }} color="secondary" gutterBottom>
        <ManageAccountsIcon />
        {t(`${translationKey}.title`)}
      </Typography>
      {!maintenances && (
        <>
          <Skeleton variant="rectangular" height={32} />
          <br />
          <Skeleton variant="rectangular" height={32} />
          <br />
          <Skeleton variant="rectangular" height={32} />
        </>
      )}
      {maintenances && maintenances.length === 0 && (
        <Alert severity="success" variant="outlined">
          {t('dashboard.expiration-maintenances.empty', {
            defaultValue: 'Nessuna manutenzione in scadenza'
          })}
        </Alert>
      )}
      {maintenances &&
        maintenances.map((maintenance) => (
          <Alert
            severity={'warning'}
            key={maintenance.id}
            className="mb-2"
            action={
              <NavLink
                to={`/tenant/equipments/maintenance?equipment_id=${maintenance.equipment_id}`}>
                <Button color="inherit" size="small">
                  {t('dashboard.expiration-maintenances.go')}
                </Button>
              </NavLink>
            }>
            {useValidityAlert
              ? t('dashboard.expiration-maintenances-validity.row-body', {
                  type: maintenance.maintenance_type?.display_name
                    ? t(
                        `entities.maintenance.maintenance_type_options.${maintenance.maintenance_type.display_name}`
                      )
                    : '',
                  code: maintenance.equipment,
                  warehouse_code: maintenance.warehouse_code || '—',
                  date: maintenance.expiry_date
                })
              : t('dashboard.expiration-maintenances.row-body', {
                  code: maintenance.equipment,
                  serial_number: maintenance.serial_number,
                  date: maintenance.expected_date || maintenance.expected_month
                })}
          </Alert>
        ))}
      <div className="flex justify-center mt-2">
        <NavLink to={viewAllHref}>
          <Button variant="outlined" size="small">
            {t('global.view-all')}
          </Button>
        </NavLink>
      </div>
    </div>
  );
}

export function ExpiringMaintenancesWithoutTypeWidget() {
  const filterParams = { maintenance_type__isnull: 'true' };
  return (
    <ExpiringMaintenancesWidgetBase
      filterParams={filterParams}
      translationKey="dashboard.expiration-maintenances-without-type"
      viewAllParams={filterParams}
    />
  );
}

export function ExpiringMaintenancesQualificationWidget() {
  const filterParams = { maintenance_type: String(MAINTENANCE_TYPE_OPTIONS[0].value) };
  return (
    <ExpiringMaintenancesWidgetBase
      filterParams={filterParams}
      translationKey="dashboard.expiration-maintenances-qualification"
      viewAllParams={filterParams}
      useValidityAlert
    />
  );
}

export function ExpiringMaintenancesProcessValidationWidget() {
  const filterParams = { maintenance_type: String(MAINTENANCE_TYPE_OPTIONS[1].value) };
  return (
    <ExpiringMaintenancesWidgetBase
      filterParams={filterParams}
      translationKey="dashboard.expiration-maintenances-process-validation"
      viewAllParams={filterParams}
      useValidityAlert
    />
  );
}

export function ExpiringMaintenancesElectricalWidget() {
  const filterParams = { maintenance_type: String(MAINTENANCE_TYPE_OPTIONS[2].value) };
  return (
    <ExpiringMaintenancesWidgetBase
      filterParams={filterParams}
      translationKey="dashboard.expiration-maintenances-electrical"
      viewAllParams={filterParams}
      useValidityAlert
    />
  );
}
